import { useMemo } from 'react'

import { getCombinedCount, getFrequencyCount } from '../../helpers/filterSquads'
import { LiveGameEco } from './LiveGameEcoUnit'
import { LiveGameEcoBuilding } from './LiveGameEcoBuilding'
import { LiveGameMilitaryBuilding } from './LiveGameMilitaryBuilding'
import { LiveGameSeperator } from './LiveGameSeperator'
import classNames from 'classnames'
import { LiveGameTechDisplay } from './LiveGameTechDisplay'
import { VizierDisplay } from './CivUnique/VizierDisplay'
import { LiveGameLandmarkDisplay } from './LiveGameLandmarkDisplay'
import { useQuery } from 'react-query'
import { LiveGameKeyTech } from './LiveGameKeyTech'
import { useSocketState } from '../../helpers/useSocket'
import { TughlaqGovernor } from './CivUnique/TughlaqGovernor'
import { getBackendUrl } from '../../helpers/url'
import { getPbgidsByType, usePbgidIcons } from '../../helpers/pbgidIcons'
import { VikingSagaProgress } from './CivUnique/VikingSagaProgress'
const upgradePBGID = require('../../config/ageUpUpgrades.json')

// Abbasid wings, Ayyubid/KT/Golden Horde upgrades that age the player up instead of a landmark
const landmarkUpgrades = upgradePBGID.flatMap((item) => item.pbg_id)

/** pbgids of the buildings shown in the overview, grouped by their unit types */
function getBuildingGroups(pbgidIcons) {
	const group = (...types) => getPbgidsByType(pbgidIcons, types)

	return {
		town_center: group('town_center'),
		exchange: group('exchange'),
		barracks: group('barracks'),
		archery_range: group('archery_range'),
		stable: group('stable'),
		scar_dock: group('scar_dock'),
		longhouses: group('longhouse_vik'),
		landmarks: [
			new Set(),
			new Set(group('age1_landmark1', 'age1_landmark2')),
			new Set(group('age2_landmark1', 'age2_landmark2')),
			new Set(group('age3_landmark1', 'age3_landmark2')),
		],
	}
}

function getLandmarks(buildings, buildingGroups) {
	let array = Array(4)
	//Dark Age will always be a Towncenter, well add it so we can have it
	array[0] = buildings.filter((b) => buildingGroups.town_center.includes(b.pbg_id)).map((b) => b.pbg_id)

	for (let age = 1; age <= 3; age++) {
		array[age] = buildings.filter((b) => buildingGroups.landmarks[age].has(b.pbg_id)).map((b) => b.pbg_id)
	}
	return array
}

export function LiveGameOverviewer({ playerData, leftSide, color }) {
	const [match] = useSocketState('Match')
	const pbgidIcons = usePbgidIcons()
	const buildingGroups = useMemo(() => getBuildingGroups(pbgidIcons), [pbgidIcons])

	const fetchCivs = async () => {
		const res = await fetch(`${getBackendUrl()}/api/config/civs`)
		return res.json()
	}

	const { data, status } = useQuery('civs', fetchCivs)

	const selectedCiv = data?.find((value) => value.aoe2cm === playerData?.civilization || value.label === playerData?.civilization)

	let playerBuildings = Object.values(playerData?.entities || [])
	let playerTechs = Object.values(playerData?.technologiesResearched || []).map(String)

	let landmarks = getLandmarks(playerBuildings, buildingGroups)
	let ageUpAsUpgrade = playerTechs.filter((t) => landmarkUpgrades.includes(parseInt(t)))

	let farmIcon = selectedCiv?.farmType || 199747

	//Leaving this here since we might want to display Docks later, but unsure
	let dockCount = 0 //getFrequencyCount(playerBuildings, buildingGroups.scar_dock)

	//Convert from the IDs that hossein used in the extract to images, remember that LUA does arrays starting to 1
	let tughlaqGovernorIds = {
		1: 'governor_armor', //Ajmer
		2: 'governor_food', //Bakkhar
		3: 'governor_cavalry', //Jalor
		4: 'governor_villager', //Multan
		5: 'governor_religious', //Sehwan
		6: 'governor_upgrade', //Uch
	}

	console.log(playerData?.tughlaq_fort)

	return (
		<div
			className={classNames(`w-[250px] h-[310px] border-solid border flex flex-col scale-90`, leftSide ? '-ml-3' : '-mr-3')}
			style={{
				borderColor: 'var(--main-color)',
				backgroundImage: 'url("/sheepCounter/background.png")',
			}}>
			<div className={classNames('flex w-auto h-[150px]', leftSide ? 'flex-row ' : 'flex-row-reverse ')}>
				<div className={classNames('flex flex-col flex-wrap gap-1 justify-center h-full', leftSide ? 'ml-1 mr-3' : 'mr-1 ml-3')}>
					<LiveGameEcoBuilding pbgid={199640} count={getFrequencyCount(playerBuildings, buildingGroups.town_center)} inQueue={0} leftSide={leftSide} showCount={true} />
					<LiveGameEcoBuilding pbgid={farmIcon} count={getFrequencyCount(playerBuildings, [farmIcon])} inQueue={0} leftSide={leftSide} showCount={true} />
					{/* Vikings has Longhouses which we sh ould add. Replacing markets with it on Vikings since they aren't really a trade civ. Might change when the meta changes */}
					{playerData?.civilization !== 'Vikings' ? (
						<LiveGameEcoBuilding pbgid={199632} count={getFrequencyCount(playerBuildings, buildingGroups.exchange)} inQueue={0} leftSide={leftSide} showCount={true} />
					) : (
						<LiveGameMilitaryBuilding
							pbgid={2596086}
							count={getFrequencyCount(playerBuildings, buildingGroups.longhouses)}
							inQueue={0}
							leftSide={leftSide}
							showCount={true}
						/>
					)}
				</div>

				<div className={classNames('w-[21px] my-auto', leftSide ? '' : '-mr-6 ml-2')}>
					<LiveGameSeperator />
				</div>

				<div className={classNames('flex flex-col flex-wrap gap-1 justify-center h-full w-1/3', leftSide ? '-ml-[12px]' : '-mr-[12px]')}>
					{playerData?.civilization === 'Macedonian Dynasty' ? (
						//This is for the Stronghold since the Macedonian don't have a barracks
						<LiveGameMilitaryBuilding pbgid={9001600} count={getFrequencyCount(playerBuildings, [9001600])} inQueue={0} leftSide={leftSide} showCount={true} />
					) : (
						<LiveGameMilitaryBuilding
							pbgid={145521}
							count={getFrequencyCount(playerBuildings, buildingGroups.barracks)}
							inQueue={0}
							leftSide={leftSide}
							showCount={true}
						/>
					)}
					<LiveGameMilitaryBuilding
						pbgid={199645}
						count={getFrequencyCount(playerBuildings, buildingGroups.archery_range)}
						inQueue={0}
						leftSide={leftSide}
						showCount={true}
					/>
					<LiveGameMilitaryBuilding pbgid={129993} count={getFrequencyCount(playerBuildings, buildingGroups.stable)} inQueue={0} leftSide={leftSide} showCount={true} />
				</div>

				<div className={classNames('flex flex-col flex-wrap gap-1 justify-start pt-[11px] h-full w-1/3 scale-90', leftSide ? '' : '')}>
					{dockCount >= 1 && <LiveGameMilitaryBuilding pbgid={199644} count={dockCount} inQueue={0} leftSide={leftSide} showCount={true} />}
					{['Abbasid Dynasty', 'Ayyubids', 'Knights Templar', 'Golden Horde'].includes(playerData?.civilization) ? (
						<>
							{ageUpAsUpgrade.map((v, i) => {
								return (
									<div className="flex flex-row justify-center upgrade">
										<LiveGameLandmarkDisplay pbgid={v} showCount={false} isKT={playerData?.civilization === 'Knights Templar'} />
									</div>
								)
							})}
						</>
					) : (
						<>
							<div className="flex flex-row justify-center">
								{playerData?.age >= 1 && landmarks[1].map((v, i) => <LiveGameLandmarkDisplay key={v} pbgid={v} showCount={false} />)}
							</div>
							<div className="flex flex-row justify-center">
								{playerData?.age >= 2 && landmarks[2].map((v, i) => <LiveGameLandmarkDisplay key={v} pbgid={v} showCount={false} />)}
							</div>
							<div className="flex flex-row justify-center">
								{playerData?.age >= 3 && landmarks[3].map((v, i) => <LiveGameLandmarkDisplay key={v} pbgid={v} showCount={false} />)}
							</div>
						</>
					)}
				</div>
			</div>

			<div className="flex flex-col px-1 -mt-1 mb-2">
				{playerData?.civilization === 'Rus' && (
					<div className={classNames('flex', leftSide ? 'flex-row' : 'flex-row-reverse')}>
						<LiveGameEcoBuilding pbgid={172205} count={getFrequencyCount(playerBuildings, [172205])} inQueue={0} leftSide={leftSide} showCount={true} />
						<div className="flex flex-row my-auto mx-4">
							<h1 className="text-white text-xl">{parseInt(playerData?.statetree?.hunting_cabin_player_gold_rate_rus)}</h1>
							<img className="w-9" src={`/liveGameIcons/gold.png`} alt="" />
							<h1 className="text-[#b1b1b1] ">/min</h1>
						</div>
					</div>
				)}
				{playerData?.civilization === 'Chinese' && (
					<div className={classNames('flex gap-[19px]', leftSide ? 'flex-row' : 'flex-row-reverse')}>
						<LiveGameEcoBuilding pbgid={156481} count={getFrequencyCount(playerBuildings, [156481])} inQueue={0} leftSide={leftSide} showCount={true} />
						<LiveGameEcoBuilding pbgid={156488} count={getFrequencyCount(playerBuildings, [156488])} inQueue={0} leftSide={leftSide} showCount={true} />
					</div>
				)}
				{/* RIP Meditation Gardens, you'll always be remembered <3 */}
				{/* {playerData?.civilization === "Zhu Xi's Legacy" && (
					<div className={classNames('flex', leftSide ? 'flex-row' : 'flex-row-reverse')}>
						{getFrequencyCount(playerBuildings, [6065722]) ? (
							<>
								<LiveGameLandmarkDisplay key={'medgarden'} pbgid={6065722} showCount={false} />
								<div className="flex flex-row my-auto mx-4 gap-1">
									{playerData.statetree.data.meditation_gardens_total_food_generation >= 2 && (
										<div className="flex flex-row">
											<h1 className="text-white text">{parseInt(playerData.statetree.data.meditation_gardens_total_food_generation)}</h1>
											<img className="w-8 h-7" src={`/liveGameIcons/food.png`} alt="" />
										</div>
									)}
									{playerData.statetree.data.meditation_gardens_total_wood_generation >= 2 && (
										<div className="flex flex-row">
											<h1 className="text-white text">{parseInt(playerData.statetree.data.meditation_gardens_total_wood_generation)}</h1>
											<img className="w-8 h-7" src={`/liveGameIcons/wood.png`} alt="" />
										</div>
									)}
									{playerData.statetree.data.meditation_gardens_total_gold_generation >= 2 && (
										<div className="flex flex-row">
											<h1 className="text-white text">{parseInt(playerData.statetree.data.meditation_gardens_total_gold_generation)}</h1>
											<img className="w-8 h-7" src={`/liveGameIcons/gold.png`} alt="" />
										</div>
									)}
									{playerData.statetree.data.meditation_gardens_total_stone_generation >= 2 && (
										<div className="flex flex-row">
											<h1 className="text-white text">{parseInt(playerData.statetree.data.meditation_gardens_total_stone_generation)}</h1>
											<img className="w-8 h-7" src={`/liveGameIcons/stone.png`} alt="" />
										</div>
									)}
								</div>
							</>
						) : (
							<></>
						)}
					</div>
				)} */}

				{playerData?.civilization === 'Vikings' && (
					<div className="flex flex-row mt-2 -mb-3 gap-[1px] -mx-[3px]">
						<VikingSagaProgress
							xp={playerData?.stateTree?.saga_serpent_progress_vik}
							useBackground={true}
							xpRequired={1}
							icon={'/liveGameIcons/unique/vikings/saga_serpent_activation.png'}
							color={color}
						/>

						<VikingSagaProgress
							xp={playerData?.stateTree?.saga_bear_progress_vik}
							useBackground={true}
							xpRequired={1}
							icon={'/liveGameIcons/unique/vikings/saga_bear_activation.png'}
							color={color}
						/>

						<VikingSagaProgress
							xp={playerData?.stateTree?.saga_falcon_progress_vik}
							xpRequired={1}
							useBackground={true}
							icon={'/liveGameIcons/unique/vikings/saga_falcon_activation.png'}
							color={color}
						/>

						<VikingSagaProgress
							xp={playerData?.stateTree?.saga_raven_progress_vik}
							xpRequired={1}
							useBackground={true}
							icon={'/liveGameIcons/unique/vikings/saga_raven_activation.png'}
							color={color}
						/>

						<VikingSagaProgress
							xp={playerData?.stateTree?.saga_sheep_progress_vik}
							xpRequired={1}
							useBackground={true}
							icon={'/liveGameIcons/unique/vikings/saga_sheep_activation.png'}
							color={color}
						/>

						<VikingSagaProgress
							xp={playerData?.stateTree?.saga_wolf_progress_vik}
							xpRequired={1}
							useBackground={true}
							icon={'/liveGameIcons/unique/vikings/saga_wolf_activation.png'}
							color={color}
						/>
					</div>
				)}
			</div>

			<div className={classNames('flex flex-col gap-4 px-4', leftSide ? '' : 'items-end')}>
				{playerData?.civilization === 'Ottomans' && <VizierDisplay upgrades={playerTechs} hasImperialCouncil={playerBuildings.includes(2055470)} leftSide={leftSide} />}

				{playerData?.civilization === 'Tughlaq Dynasty' && (
					<div className="flex flex-row flex-wrap gap-[2px]">
						{playerData?.tughlaq_fort?.map((governor, i) => (
							<TughlaqGovernor governor={tughlaqGovernorIds[governor['2']]} level={governor['3']} leftSide={leftSide} />
						))}
					</div>
				)}

				{/* Used for Macedonian since they have some "keyTechs" that has levels to them that we want to show in the overview */}
				{playerData?.civilization === 'Macedonian Dynasty' && (
					<div className={classNames('flex flex-wrap gap-1', leftSide ? 'flex-row' : 'flex-row-reverse')}>
						{selectedCiv?.hiddenKeyTechs.map((tech, index) => (
							<LiveGameKeyTech key={index} color={color} tech={tech} currentUpgrades={playerData?.technologiesResearched} />
						))}
					</div>
				)}

				{selectedCiv?.uniqueTechs.map((row, rowIndex) => (
					<div key={rowIndex} className={classNames('flex flex-wrap gap-1', leftSide ? 'flex-row' : 'flex-row-reverse')}>
						{row.map((tech, i) => (
							<LiveGameTechDisplay key={tech + String(i)} pbg_id={tech} hasTech={playerTechs.includes(String(tech))} />
						))}
					</div>
				))}
				{/* This is used for French and JD where their unique techs are in 2 places with seperate ID's */}
				<div className={classNames('flex flex-wrap gap-1', leftSide ? 'flex-row' : 'flex-row-reverse')}>
					{selectedCiv?.duplicateUniqueTechs?.map((tech, i) => (
						<LiveGameTechDisplay key={tech.name + String(i)} pbg_id={tech.ids[0]} hasTech={tech.ids.some((t) => playerTechs.includes(String(t)))} />
					))}
				</div>
			</div>
		</div>
	)
}
